import React, { useMemo } from 'react';

import { TextListNumberIcon, TextListBulletIcon, RenderIconType } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import { DropdownButton } from '@adsk/alloy-react-menu';

import { CheckIcon } from './styles';

export const AUTO_NUMBERING_MODE = { SEQUENCE: 'sequence', SAME: 'same' };
type ModePickerType = {
  style?: React.CSSProperties;
  /** The current mode of the sequence */
  currentMode: string;
  /** The sequence key */
  sequenceKey: string;
  /** The handler for the mode change, with the shape of (sequenceKey: string, mode: string) => void */
  onSequenceModeChange: (sequenceKey: string, mode: string) => void;
};

const ModePicker = React.forwardRef<HTMLButtonElement, ModePickerType>(
  ({ currentMode, sequenceKey, onSequenceModeChange, style, ...rest }, ref) => {
    const { T } = useLocale(({ locale }) => require(`../locale/${locale}.json`));
    const options = useMemo(
      () => [
        {
          renderIcon: TextListBulletIcon.renderIcon,
          key: AUTO_NUMBERING_MODE.SAME,
          rightSideLabel: currentMode === AUTO_NUMBERING_MODE.SAME ? <CheckIcon /> : null,
          label: T.translate('same')
        },
        {
          renderIcon: TextListNumberIcon.renderIcon,
          key: AUTO_NUMBERING_MODE.SEQUENCE,
          rightSideLabel: currentMode === AUTO_NUMBERING_MODE.SEQUENCE ? <CheckIcon /> : null,
          label: T.translate('sequence')
        }
      ],
      [currentMode, T]
    );
    const selectedOption:
      | {
          renderIcon: RenderIconType;
          key: string;
          label: React.ReactNode;
          rightSideLabel?: React.ReactNode;
        }
      | undefined = useMemo(() => options.find(({ key }) => key === currentMode), [options, currentMode]);

    return (
      <DropdownButton
        ref={ref as React.Ref<HTMLButtonElement & HTMLDivElement>}
        size={DropdownButton.SIZES.SMALL}
        variant={DropdownButton.VARIANTS.TERTIARY}
        menuStyle={{ minWidth: '210px', maxWidth: '300px', width: 'min-content' }}
        options={options}
        renderIcon={selectedOption?.renderIcon}
        onChange={([mode]) => onSequenceModeChange(sequenceKey, mode)}
        style={style}
        {...rest}
      >
        {selectedOption?.label}
      </DropdownButton>
    );
  }
);

ModePicker.displayName = 'ModePicker';

export default ModePicker;
